import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import "./Class.css";
import Footer from "../../Components/Footer/Footer";
import VideoSection from "../../Components/VideoSection/VideoSection";
import LessonSection from "../../Components/LessonSection/LessonSection";
import MenuTab from "../../Components/MenuTab/MenuTab";
import ClassProgress from "../../Components/ClassProgress/ClassProgress";
import ClassLoading from "../../Components/ClassLoading/ClassLoading";
import VideoSubscription from "../../Components/VideoSubscription/VideoSubscription";
import axiosInstance from "../../Config/axiosConfig";

function Class() {
  const { classId } = useParams();
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [classInfo, setClassInfo] = useState({ 
    description: "", 
    resource: "", 
    duration: "",
    subscribed: false
  });
  const [loading, setLoading] = useState(true);
  const [showSubscription, setShowSubscription] = useState(false);
  const [firstVideoPlayed, setFirstVideoPlayed] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setLoading(true);

    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem("deviceId");
    const storedUserIp = localStorage.getItem("userIp");

    axiosInstance.get(`/class/get-class/${classId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
          UserDevice: storedDeviceId,
          UserIPAddress: storedUserIp,
        },
      })
      .then((response) => {
        const data = response.data;
        if (data.code === 200 && data.data) {
          setVideos(data.data.videos || []);
          setSelectedVideo(data.data.videos ? data.data.videos[0] : null);
          setClassInfo({
            description: data.data.description,
            resource: data.data.resource,
            duration: data.data.duration,
            instructor: data.data.instructor,
            instructor_title: data.data.instructor_title,
            about_teacher: data.data.about_teacher,
            instructor_photo: data.data.instructor_photo,
            education_class: data.data.education_class,
            title: data.data.title,
            enrolled: data.data.enrolled,
            views: data.data.views,
            cost: data.data.cost,
            thumbnail: data.data.thumbnail,
            subscribed: data.data.subscribed || false
          });
        }
      })
      .catch((error) => console.error("Error fetching class data:", error))
      .finally(() => setLoading(false));
  }, [classId]);

  const handleVideoSelect = (video) => {
    if (video && video.id !== selectedVideo?.id) {
      // Allow selection if subscribed OR if selecting the first video
      if (classInfo.subscribed || video.id === videos[0]?.id) {
        setSelectedVideo(video);
      } else {
        setShowSubscription(true);
      }
    }
  };

  const handleVideoEnd = () => {
    // Mark first video as played when it ends
    if (selectedVideo?.id === videos[0]?.id) {
      setFirstVideoPlayed(true);
      // Show subscription if not subscribed
      if (!classInfo.subscribed) {
        setShowSubscription(true);
      }
    }
    
    // Auto-play next video only if subscribed
    if (classInfo.subscribed) {
      const currentIndex = videos.findIndex(v => v.id === selectedVideo.id);
      if (currentIndex < videos.length - 1) {
        setSelectedVideo(videos[currentIndex + 1]);
      }
    }
  };

  const handleSubscribeSuccess = () => {
    setClassInfo(prev => ({ ...prev, subscribed: true }));
    setShowSubscription(false);
    // After subscription, play the next video if first was already played
    if (firstVideoPlayed && videos.length > 1) {
      setSelectedVideo(videos[1]);
    }
  };

  const progress = classInfo.subscribed && videos.length > 0 
    ? (videos.findIndex(v => v.id === selectedVideo?.id) + 1) / videos.length * 100 
    : firstVideoPlayed ? (1 / videos.length) * 100 : 0;

  if (loading) {
    return <ClassLoading />;
  }

  return (
    <div className="class-container">
      <div className="class-container-section">
        <div className="content-section">
          {showSubscription ? (
            <VideoSubscription
              classData={classInfo}
              classId={classId} 
              onSubscribe={handleSubscribeSuccess} 
            />
          ) : (
            <VideoSection 
              selectedVideo={selectedVideo}
              classData={classInfo}
              classId={classId}
              title={classInfo.title} 
              instructor={classInfo.instructor}
              instructor_title={classInfo.instructor_title}
              instructor_photo={classInfo.instructor_photo}
              onVideoEnd={handleVideoEnd}
              autoPlay={selectedVideo !== videos[0]}
              ref={videoRef}
            />
          )}
            
          <LessonSection 
            subtopics={videos} 
            onSelect={handleVideoSelect} 
            selectedVideo={selectedVideo}
            isSubscribed={classInfo.subscribed}
          />
        </div>
        <div className="content-menu-section">
          <MenuTab 
            description={classInfo.description} 
            resource={classInfo.resource}
          />
          <ClassProgress 
            subtopics={videos} 
            progress={progress} 
            duration={classInfo.duration} 
            instructor={classInfo.instructor}
            instructor_title={classInfo.instructor_title}
            about_teacher={classInfo.about_teacher} 
            education_class={classInfo.education_class}
            enrolled={classInfo.enrolled}
            views={classInfo.views}
            isSubscribed={classInfo.subscribed}
          />
        </div>
      </div>
      <Footer className='class-class-progress'/>
    </div>
  );
}

export default Class;